import React, { useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import * as Icon from 'react-feather';
import SidebarLink from './SidebarLink';
import { useAuth } from '../../AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const LOGO = 'https://cdn2.schedpilot.com/files/logo.png';

// Portal tooltip — renders into document.body to escape overflow:hidden clipping
function MiniTooltip({ anchorRef, label }) {
  const rect = anchorRef.current?.getBoundingClientRect();
  if (!rect) return null;
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      left: rect.right + 8,
      top: rect.top + rect.height / 2,
      transform: 'translateY(-50%)',
      background: '#1e293b',
      color: '#fff',
      padding: '4px 10px',
      borderRadius: 6,
      fontSize: '0.75rem',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      zIndex: 9999,
      pointerEvents: 'none',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      {label}
    </div>,
    document.body
  );
}

function MiniNavItem({ link, label, isActive, children }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  return (
    <div ref={ref} style={{ width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <Link to={link} style={{
        width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '10px 0', textDecoration: 'none',
        color: isActive ? '#2fba01' : '#444',
        borderLeft: `3px solid ${isActive ? '#2fba01' : 'transparent'}`,
        background: isActive ? '#fff' : 'transparent',
      }}>
        {children}
      </Link>
      {hovered && <MiniTooltip anchorRef={ref} label={label} />}
    </div>
  );
}

function MiniButtonItem({ onClick, label, children }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  return (
    <div ref={ref} style={{ width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <button onClick={onClick} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        width: '100%', padding: '10px 0',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#666',
      }}>
        {children}
      </button>
      {hovered && <MiniTooltip anchorRef={ref} label={label} />}
    </div>
  );
}

const MINI_NAV = [
  { link: '/',               id: 'dashboard',      icon: Icon.Sliders,    label: 'Dashboard'       },
  { link: '/publish',        id: 'publish',        icon: Icon.Send,       label: 'Publish'         },
  { link: '/calendar-home',  id: 'calendar-home',  icon: Icon.Calendar,   label: 'Calendar'        },
  { link: '/media-library',  id: 'media-library',  icon: Icon.Image,      label: 'Media Library'   },
  { link: '/workspaces',     id: 'workspaces',     icon: Icon.Grid,       label: 'Workspaces'      },
  // { link: '/content-center', id: 'content-center', icon: Icon.Layers,     label: 'Content Center'  }, // hidden for now
  { link: '/creator-studio', id: 'creator-studio', icon: Icon.Layout,     label: 'Creator Studio'  },
  { link: '/analytics',      id: 'analytics',      icon: Icon.BarChart,   label: 'Analytics'       },
  { link: '/schedule-posts', id: 'scheduled',      icon: Icon.FileText,   label: 'Posts'           },
  { link: '/social-accounts',id: 'social-accounts',icon: Icon.User,       label: 'Accounts'        },
  { link: '/team',           id: 'team',           icon: Icon.Users,      label: 'Team'            },
  { link: '/api-access',     id: 'api-access',     icon: Icon.Cpu,        label: 'API Access'      },
  { link: '/affiliate',      id: 'affiliate',      icon: Icon.TrendingUp, label: 'Affiliate'       },
  { link: '/billing',        id: 'billing',        icon: Icon.CreditCard, label: 'Billing'         },
  { link: '/settings',       id: 'settings',       icon: Icon.Settings,   label: 'Settings'        },
  { link: '/help',           id: 'help',           icon: Icon.HelpCircle, label: 'Help'            },
];

function SideMenu({activeItem, collapsed}) {
  const myContext = useAuth();
  const navigate = useNavigate();

  const postsSubItems = ['scheduled', 'posted-items', 'failed', 'drafts'];
  const [postsOpen, setPostsOpen] = useState(postsSubItems.includes(activeItem));

  const isMembershipValid = myContext.isValidMembership();
  const isCancelled = myContext.membership?.subscription_status === "cancelled";
  const hasNoMembership = myContext.membership?.zeroMembership === "yes";

  // Lifetime members are always treated as fully active — no "restricted"
  // or "renew" states, even if their row was somehow marked cancelled.
  const isLifetime = myContext.membership?.plan?.type === "lifetime" || (
    myContext.membership?.end_date_time
      ? new Date(myContext.membership.end_date_time.replace(' ', 'T') + 'Z').getTime() > Date.now() + 10 * 365 * 24 * 3600 * 1000
      : false
  );
  const showDanger     = (!isMembershipValid || isCancelled) && !isLifetime;
  const showRestricted = (!isMembershipValid || hasNoMembership) && !isLifetime;

  const doLogout = async () => {
    try { await fetch('https://api.schedpilot.com/wp-json/api/v1/logout', { method: 'GET', credentials: 'include' }); } catch {}
    localStorage.removeItem('myLoggedInUser2');
    window.location.href = '/';
  };

  // ── Mini (icon-only) sidebar — rendered without simplebar wrappers ──────────
  if (collapsed) {
    return (
      <nav id="sidebar" style={{
        width: 64, minWidth: 64, maxWidth: 64, flexShrink: 0,
        background: '#f5f5f5', borderRight: '1px solid #d0d0d0',
        height: '100vh', position: 'sticky', top: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        overflowY: 'auto', overflowX: 'hidden',
      }}>
        {/* Logo */}
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer', padding: '14px 0 6px' }}>
          <img src={LOGO} alt="SchedPilot" style={{ width: 32, height: 32 }} />
        </div>

        {/* Create Post */}
        <MiniButtonItem onClick={() => navigate('/write-post')} label="Create Post">
          <span style={{
            width: 34, height: 34, borderRadius: 6, flexShrink: 0,
            background: '#2fba01', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon.Edit2 size={15} color="#fff" />
          </span>
        </MiniButtonItem>

        {/* Nav icons */}
        {MINI_NAV.map(({ link, id, icon: Ico, label }) => {
          const isActive = activeItem === id || (id === 'scheduled' && postsSubItems.includes(activeItem));
          return (
            <MiniNavItem key={id} link={link} label={label} isActive={isActive}>
              <Ico size={18} />
            </MiniNavItem>
          );
        })}

        {/* Logout */}
        <div style={{ marginTop: 'auto', width: '100%' }}>
          <MiniButtonItem onClick={doLogout} label="Logout">
            <Icon.LogOut size={18} />
          </MiniButtonItem>
        </div>
      </nav>
    );
  }

  // ── Full sidebar ─────────────────────────────────────────────────────────────
  return (
    <nav id="sidebar" className="sidebar js-sidebar">
      <div className="sidebar-content js-simplebar" data-simplebar="init">
        <div className="simplebar-mask">
          <div className="simplebar-offset" style={{right: "0",  bottom: "0"}}>
            <div className="simplebar-content-wrapper" tabIndex="0" role="region" style={{ height: "100%", overflowX: "hidden", overflowY: "auto" }}>
              <div className="simplebar-content" style={{padding: "0px", display: 'flex', flexDirection: 'column', minHeight: '100%'}}>

                <div className="flex-grow-1">
                  <a className="sidebar-brand d-flex align-items-center" onClick={() => navigate("/")}>
                    <img src={LOGO} alt="SchedPilot Icon" style={{width: 32, height: 32, marginRight: 8}} />
                    SchedPilot
                  </a>

                  <ul className="sidebar-nav">
                    <li className='sidebar-item mb-3 text-center px-3 mt-3'>
                      <button onClick={() => navigate("/write-post")} className='btn w-100 btn-primary'>
                        <i className="fas fa-pencil-alt me-2"></i> Create Post
                      </button>
                    </li>

                    <li className="sidebar-header">Dashboard</li>
                    <SidebarLink linkTitle={"Dashboard"} link={"/"} active={activeItem} id={"dashboard"}><Icon.Sliders /></SidebarLink>
                    <SidebarLink linkTitle={"Publish"} link={"/publish"} active={activeItem} id={"publish"}><Icon.Send /></SidebarLink>
                    <SidebarLink linkTitle={"Calendar"} link={"/calendar-home"} active={activeItem} id={"calendar-home"}><Icon.Calendar /></SidebarLink>
                    <SidebarLink linkTitle={"Media Library"} link={"/media-library"} active={activeItem} id={"media-library"}><Icon.Image /></SidebarLink>
                    <SidebarLink linkTitle={"Workspaces"} link={"/workspaces"} active={activeItem} id={"workspaces"}><Icon.Grid /></SidebarLink>
                    {/* <SidebarLink linkTitle={"Content Center"} link={"/content-center"} active={activeItem} id={"content-center"}><Icon.Layers /></SidebarLink> */}
                    <SidebarLink linkTitle={"Creator Studio"} link={"/creator-studio"} active={activeItem} id={"creator-studio"}><Icon.Layout /></SidebarLink>
                    <SidebarLink linkTitle={"Analytics"} link={"/analytics"} active={activeItem} id={"analytics"}><Icon.BarChart /></SidebarLink>

                    {/* Posts collapsible group */}
                    <li className={"sidebar-item" + (postsSubItems.includes(activeItem) ? " active" : "")}>
                      <a className="sidebar-link" onClick={() => setPostsOpen(o => !o)}
                        style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Icon.FileText size={18} className="align-middle" />
                          <span className="align-middle">Posts</span>
                        </span>
                        <Icon.ChevronRight size={14} style={{ transition: 'transform 0.2s', transform: postsOpen ? 'rotate(90deg)' : 'none', opacity: 0.5 }} />
                      </a>
                    </li>
                    {postsOpen && (
                      <li style={{ listStyle: 'none' }}>
                        <ul className="sidebar-nav" style={{ paddingLeft: 0 }}>
                          <li className={"sidebar-item" + (activeItem === 'scheduled' ? " active" : "")}>
                            <Link className="sidebar-link" to="/schedule-posts" style={{ paddingLeft: '2.75rem' }}>
                              <Icon.Clock size={15} className="align-middle" /> <span className="align-middle">Scheduled</span>
                            </Link>
                          </li>
                          <li className={"sidebar-item" + (activeItem === 'posted-items' ? " active" : "")}>
                            <Link className="sidebar-link" to="/posted-items" style={{ paddingLeft: '2.75rem' }}>
                              <Icon.CheckSquare size={15} className="align-middle" /> <span className="align-middle">Posted</span>
                            </Link>
                          </li>
                          <li className={"sidebar-item" + (activeItem === 'failed' ? " active" : "")}>
                            <Link className="sidebar-link" to="/failed" style={{ paddingLeft: '2.75rem' }}>
                              <Icon.AlertCircle size={15} className="align-middle" /> <span className="align-middle">Failed</span>
                            </Link>
                          </li>
                          <li className={"sidebar-item" + (activeItem === 'drafts' ? " active" : "")}>
                            <Link className="sidebar-link" to="/drafts" style={{ paddingLeft: '2.75rem' }}>
                              <Icon.File size={15} className="align-middle" /> <span className="align-middle">Drafts</span>
                            </Link>
                          </li>
                        </ul>
                      </li>
                    )}

                    <li className="sidebar-header">Social Accounts</li>
                    <SidebarLink linkTitle={"Accounts"} link={"/social-accounts"} active={activeItem} id={"social-accounts"}><Icon.User /></SidebarLink>

                    <li className="sidebar-header">Account</li>
                    <SidebarLink linkTitle={"Team"} link={"/team"} active={activeItem} id={"team"}><Icon.Users /></SidebarLink>
                    <SidebarLink linkTitle={"API Access"} link={"/api-access"} active={activeItem} id={"api-access"}><Icon.Cpu /></SidebarLink>
                    <SidebarLink linkTitle={"Affiliate"} link={"/affiliate"} active={activeItem} id={"affiliate"}><Icon.TrendingUp /></SidebarLink>
                    <SidebarLink linkTitle={"Billing"} link={"/billing"} active={activeItem} id={"billing"}><Icon.CreditCard /></SidebarLink>
                    <SidebarLink linkTitle={"Settings"} link={"/settings"} active={activeItem} id={"settings"}><Icon.Settings /></SidebarLink>
                    <SidebarLink linkTitle={"Help"} link={"/help"} active={activeItem} id={"help"}><Icon.HelpCircle /></SidebarLink>

                    <li className={"sidebar-item"}>
                      <a className="sidebar-link" onClick={doLogout}>
                        <Icon.LogOut className="align-middle" /> <span className="align-middle">Logout</span>
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Membership status box */}
                <div className="mt-auto p-3">
                  <div className={`card mb-0 shadow-none ${showDanger ? 'bg-danger-light text-danger' : 'bg-light'}`}
                       style={{ borderRadius: '10px', backgroundColor: showDanger ? '#fff5f5' : '#f8f9fa' }}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center mb-2">
                        <div className={`rounded-circle me-2 ${showDanger ? 'bg-danger' : 'bg-success'}`} style={{ width: '8px', height: '8px' }}></div>
                        <span className="small fw-bold text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>
                          {showRestricted ? 'No Active Plan' : isLifetime ? 'Lifetime Member' : isCancelled ? 'Ending Soon' : 'Pro Member'}
                        </span>
                      </div>
                      <p className="small mb-2 text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                        {showRestricted
                          ? 'Your access is restricted.'
                          : `Plan: ${myContext.membership?.plan?.plan || 'Active'}${isLifetime ? ' (Lifetime)' : ''}`}
                      </p>
                      <button
                        onClick={() => navigate("/billing")}
                        className={`btn btn-sm w-100 fw-bold ${showDanger ? 'btn-danger' : 'btn-outline-primary'}`}
                        style={{ fontSize: '0.7rem' }}
                      >
                        {showDanger ? 'Renew Now' : 'Manage Billing'}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default SideMenu
